import { useLocalization } from "../../../features/localization/index.js";
import { useFileDialog } from "../../../../hooks/use-file-dialog.js";
import CodeInput_default from "../../../components/code-input/CodeInput.vue.js";
import DataTable_default from "../../../components/data-table/DataTable.vue.js";
import DataTableHeader_default from "../../../components/data-table/DataTableHeader.vue.js";
import DataTableRow_default from "../../../components/data-table/DataTableRow.vue.js";
import { getFileName } from "../helpers/files.js";
import { getFormBodyRows, getFormBodyValue } from "../helpers/get-form-body-rows.js";
import RequestBodyForm_default from "./RequestBodyForm.vue.js";
import { getStructuredBodyCodec } from "../helpers/structured-body-codec.js";
import RequestBodyStructured_default from "./RequestBodyStructured.vue.js";
import RequestBodyViewToggle_default from "./RequestBodyViewToggle.vue.js";
import CollapsibleSection_default from "../../../components/layout/CollapsibleSection.vue.js";
import { Fragment, computed, createBlock, createCommentVNode, createElementBlock, createElementVNode, createTextVNode, createVNode, defineComponent, openBlock, ref, toDisplayString, unref, watch, withCtx, withModifiers } from "vue";
import { ScalarButton } from "@scalar/components/button";
import { ScalarListbox } from "@scalar/components/listbox";
import { ScalarIconMagicWand } from "@scalar/icons";
import { objectEntries } from "@scalar/helpers/object/object-entries";
import { ScalarIcon } from "@scalar/components/icon";
import { ScalarIconButton } from "@scalar/components/icon-button";
import { getExampleFromBody, getSchemaExampleFromBody, getSelectedBodyContentType } from "@scalar/workspace-store/request-example";
import { parseMimeType } from "@scalar/helpers/http/mime-type";
import { unpackProxyObject } from "@scalar/workspace-store/helpers/unpack-proxy";
import { Schema } from "@scalar/blocks/schema";
import { ScalarCodeBlockCopy } from "@scalar/components/code-block";
import { ScalarCopyBackdrop } from "@scalar/components/copy";
import { CONTENT_TYPES } from "@scalar/helpers/http/content-types";
import { isXmlMediaType } from "@scalar/helpers/http/is-xml-media-type";
import { isObject } from "@scalar/helpers/object/is-object";
import { getExampleValue, getExplicitExampleText } from "@scalar/workspace-store/helpers/get-example-value";
import { resolve } from "@scalar/workspace-store/resolve";
import { isObjectSchema } from "@scalar/workspace-store/schemas/v3.2/strict/type-guards";
//#region src/v2/blocks/request-block/components/RequestBody.vue?vue&type=script&setup=true&lang.ts
var _hoisted_1 = { class: "flex items-center gap-1" };
var _hoisted_2 = {
	key: 0,
	class: "min-w-0 border-t py-3 ps-10 pe-6"
};
var _hoisted_3 = {
	key: 1,
	class: "text-c-3 flex min-h-10 w-full items-center justify-center border-t p-2 text-sm"
};
var _hoisted_4 = {
	key: 2,
	class: "flex items-center justify-center overflow-hidden border-t p-1.5"
};
var _hoisted_5 = { class: "text-c-2 w-full max-w-full overflow-hidden rounded border px-1.5 py-1 text-xs whitespace-nowrap" };
var _hoisted_6 = {
	key: 1,
	class: "group/code-block relative min-w-0 border-t"
};
var RequestBody_vue_vue_type_script_setup_true_lang_default = /*@__PURE__*/ defineComponent({
	__name: "RequestBody",
	props: {
		requestBody: {},
		exampleKey: {},
		title: {},
		environment: {},
		openapiVersion: {},
		requestBodyCompositionSelection: {},
		defaultView: { default: "raw" }
	},
	emits: [
		"update:contentType",
		"update:value",
		"update:formValue",
		"generate:example"
	],
	setup(__props, { emit: __emit }) {
		const emits = __emit;
		const { translate } = useLocalization();
		const contentTypeToLanguageMap = {
			"application/json": "json",
			"application/xml": "xml",
			"application/yaml": "yaml"
		};
		/** Selected content type with default */
		const selectedContentType = computed(() => getSelectedBodyContentType(__props.requestBody, __props.exampleKey) ?? "none");
		/** Keep the editor and copy label aligned with the selected content type. */
		const selectedLanguage = computed(() => isXmlMediaType(selectedContentType.value) ? "xml" : contentTypeToLanguageMap[selectedContentType.value] ?? "plaintext");
		/**
		* Strips MIME parameters (such as `charset=utf-8`) for dropdown labels, aligned with how we treat
		* media types elsewhere (see api-reference `normalizeMimeType` / WHATWG essence).
		*/
		const contentTypeLabel = (raw) => parseMimeType(raw).essence;
		/**
		* Build the dropdown options for the content type selector.
		*
		* The list is composed of two groups:
		*   1. The built-in content types we ship friendly labels for (JSON, XML, Multipart Form, etc.).
		*   2. Any additional content types defined on the OpenAPI request body that are not in the built-in list
		*      (for example `text/csv`, `application/pdf`, vendor types like `application/vnd.api+json`, ...).
		*
		* The OpenAPI-defined extras are appended at the bottom so the well-known options stay on top, and
		* users can always pick the exact content type the operation actually accepts.
		*/
		/** MIME values remain stable while their human-readable labels follow the locale. */
		const localizedContentTypes = computed(() => ({
			...CONTENT_TYPES,
			"multipart/form-data": translate("apiClient.requestBody.multipartForm"),
			"application/x-www-form-urlencoded": translate("apiClient.requestBody.formUrlEncoded"),
			"application/octet-stream": translate("apiClient.requestBody.binaryFile"),
			"other": translate("apiClient.requestBody.other"),
			"none": translate("apiClient.requestBody.none")
		}));
		const contentTypeOptions = computed(() => {
			const builtIn = objectEntries(localizedContentTypes.value).map(([id, label]) => ({
				id,
				label
			}));
			const extras = Object.keys(__props.requestBody?.content ?? {}).filter((type) => {
				if (type in CONTENT_TYPES) return false;
				return !(contentTypeLabel(type) in CONTENT_TYPES);
			}).map((type) => ({
				id: type,
				label: contentTypeLabel(type)
			}));
			return [...builtIn, ...extras];
		});
		const selectedContentTypeModel = computed({
			get: () => {
				const found = contentTypeOptions.value.find((it) => it.id === selectedContentType.value);
				if (found) return found;
				const essence = contentTypeLabel(selectedContentType.value);
				const friendly = localizedContentTypes.value[essence] ?? essence;
				return {
					id: selectedContentType.value,
					label: friendly
				};
			},
			set: (v) => {
				emits("update:contentType", { value: v.id });
			}
		});
		function handleFileUpload(callback) {
			const { open } = useFileDialog({
				onChange: (files) => {
					const file = files?.[0];
					if (file) callback(file);
				},
				multiple: false,
				accept: "*/*"
			});
			open();
		}
		/** Dereferenced example */
		const example = computed(() => __props.requestBody && getExampleFromBody(__props.requestBody, selectedContentType.value, __props.exampleKey, __props.requestBodyCompositionSelection, __props.openapiVersion));
		/** Convert the example value to a string for the code editor */
		const bodyValue = computed(() => {
			if (!example.value) return "";
			const selected = getExampleValue(example.value);
			const explicitText = getExplicitExampleText(selected, selectedContentType.value, 2);
			if (explicitText !== void 0) return explicitText;
			const value = selected?.value;
			if (typeof value === "string") return value;
			return JSON.stringify(value, null, 2);
		});
		/** Resolved schema for the request body */
		const bodySchema = computed(() => {
			return resolve.schema(__props.requestBody?.content?.[selectedContentType.value]?.schema ?? __props.requestBody?.content?.[selectedContentType.value]?.itemSchema);
		});
		/** Codec for structured (JSON/YAML) bodies, undefined for everything else */
		const structuredCodec = computed(() => selectedContentType.value === "none" ? void 0 : getStructuredBodyCodec(selectedContentType.value));
		/** Parsed body value for the form view — `ok: false` when the raw text is not parseable */
		const parsedBody = computed(() => {
			const codec = structuredCodec.value;
			if (!codec) return { ok: false };
			const selected = getExampleValue(example.value ?? void 0);
			if (example.value?.dataValue !== void 0) return {
				ok: true,
				value: example.value.dataValue
			};
			const raw = selected?.value;
			if (raw === void 0 || raw === null || raw === "") return {
				ok: true,
				value: {}
			};
			if (typeof raw === "string") try {
				return {
					ok: true,
					value: codec.parse(raw)
				};
			} catch {
				return { ok: false };
			}
			return {
				ok: true,
				value: raw
			};
		});
		/**
		* An edited example normally takes precedence over schema-generated data. When a different
		* composition branch is selected, however, keeping that example leaves the editor showing the
		* previous branch. Track the selection itself instead of inspecting discriminator fields so this
		* also works for compositions whose members are distinguished only by their shape.
		*/
		const compositionSelectionKey = computed(() => JSON.stringify(__props.requestBodyCompositionSelection ?? {}));
		watch([
			() => __props.requestBody,
			() => __props.exampleKey,
			compositionSelectionKey
		], ([, , selection], [previousRequestBody, previousExampleKey, previousSelection]) => {
			if (__props.requestBody !== previousRequestBody || __props.exampleKey !== previousExampleKey || !(selection !== previousSelection) || previousSelection === "{}" || Object.keys(__props.requestBodyCompositionSelection ?? {}).length === 0) return;
			const codec = structuredCodec.value;
			const isXml = isXmlMediaType(selectedContentType.value);
			const isForm = selectedContentType.value === "multipart/form-data" || selectedContentType.value === "application/x-www-form-urlencoded";
			if (!__props.requestBody || !codec && !isXml && !isForm) return;
			const selectedValue = getSchemaExampleFromBody(__props.requestBody, selectedContentType.value, __props.requestBodyCompositionSelection, __props.openapiVersion);
			if (isForm) {
				const rows = getFormBodyRows({ value: selectedValue }, selectedContentType.value, bodySchema.value, __props.requestBodyCompositionSelection);
				emits("update:formValue", {
					contentType: selectedContentType.value,
					payload: rows.map((row) => ({
						name: row.name,
						value: selectedContentType.value === "multipart/form-data" ? getFormBodyValue(row) : row.value ?? void 0,
						isDisabled: row.isDisabled ?? false,
						...row.isDisabledByDefault ? { isDisabledByDefault: true } : {},
						...row.isArray ? { isArray: true } : {}
					}))
				});
				return;
			}
			let payload = "";
			if (selectedValue != null) payload = isXml ? String(selectedValue) : codec.stringify(selectedValue);
			emits("update:value", {
				payload,
				contentType: selectedContentType.value
			});
		});
		/** The form view only works on an object-shaped body root */
		const isFormViewAvailable = computed(() => parsedBody.value.ok && isObject(parsedBody.value.value));
		/**
		* Show the Form/Raw toggle for structured bodies that are (or per schema should be)
		* an object at the root. Non-object roots (arrays, primitives) stay raw-only.
		*/
		const showBodyViewToggle = computed(() => Boolean(structuredCodec.value) && (isFormViewAvailable.value || Boolean(bodySchema.value && isObjectSchema(bodySchema.value))));
		/** Selected body view, seeded from the document default (raw unless configured) */
		const bodyView = ref(__props.defaultView);
		/** Schema inspection is available for every media type with a schema, independently of its example. */
		const isSchemaViewAvailable = computed(() => selectedContentType.value !== "none" && bodySchema.value !== void 0);
		watch(() => __props.requestBody, () => {
			if (bodyView.value === "schema") bodyView.value = isFormViewAvailable.value ? __props.defaultView : "raw";
		});
		watch(isSchemaViewAvailable, (available) => {
			if (!available && bodyView.value === "schema") bodyView.value = "raw";
		});
		watch(isFormViewAvailable, (ok) => {
			if (!ok && bodyView.value === "form") bodyView.value = "raw";
		}, { immediate: true });
		/**
		* Whether we can offer to generate a fresh example from the schema.
		*
		* Only structured (JSON/YAML) bodies qualify: they have a schema to generate from and a text
		* representation we can seed the new example with. Binary and form bodies have their own editors,
		* so the button would have nothing meaningful to write.
		*/
		const canGenerateExample = computed(() => Boolean(structuredCodec.value && bodySchema.value));
		return (_ctx, _cache) => {
			return openBlock(), createBlock(unref(CollapsibleSection_default), null, {
				title: withCtx(() => [createTextVNode(toDisplayString(__props.title), 1)]),
				default: withCtx(() => [createVNode(unref(DataTable_default), {
					columns: [""],
					presentational: ""
				}, {
					default: withCtx(() => [createVNode(unref(DataTableHeader_default), { class: "relative col-span-full flex h-8 cursor-pointer items-center justify-between border-r-0 !p-0" }, {
						default: withCtx(() => [createVNode(unref(ScalarListbox), {
							modelValue: selectedContentTypeModel.value,
							"onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => selectedContentTypeModel.value = $event),
							options: contentTypeOptions.value,
							teleport: ""
						}, {
							default: withCtx(() => [createVNode(unref(ScalarButton), {
								class: "text-c-2 hover:text-c-1 flex h-full w-fit gap-1.5 px-3 font-normal",
								fullWidth: "",
								variant: "ghost"
							}, {
								default: withCtx(() => [createElementVNode("span", null, toDisplayString(selectedContentTypeModel.value.label), 1), createVNode(unref(ScalarIcon), {
									icon: "ChevronDown",
									size: "md"
								})]),
								_: 1
							})]),
							_: 1
						}, 8, ["modelValue", "options"]), createElementVNode("div", _hoisted_1, [canGenerateExample.value ? (openBlock(), createBlock(unref(ScalarIconButton), {
							key: 0,
							"aria-label": "Generate example from schema",
							icon: unref(ScalarIconMagicWand),
							label: "Generate example from schema",
							size: "sm",
							tooltip: "",
							onClick: _cache[1] || (_cache[1] = withModifiers(($event) => emits("generate:example", { contentType: selectedContentType.value }), ["stop"]))
						}, null, 8, ["icon"])) : createCommentVNode("", true), showBodyViewToggle.value || isSchemaViewAvailable.value ? (openBlock(), createBlock(RequestBodyViewToggle_default, {
							key: 1,
							disabled: !isFormViewAvailable.value,
							modelValue: bodyView.value,
							rawLabel: structuredCodec.value ? void 0 : unref(translate)("apiClient.requestBodyStructured.body"),
							showForm: showBodyViewToggle.value,
							showSchema: isSchemaViewAvailable.value,
							"onUpdate:modelValue": _cache[2] || (_cache[2] = (v) => bodyView.value = v)
						}, null, 8, [
							"disabled",
							"modelValue",
							"rawLabel",
							"showForm",
							"showSchema"
						])) : createCommentVNode("", true)])]),
						_: 1
					}), createVNode(unref(DataTableRow_default), null, {
						default: withCtx(() => [bodyView.value === "schema" && isSchemaViewAvailable.value ? (openBlock(), createElementBlock("div", _hoisted_2, [(openBlock(), createBlock(unref(Schema), {
							key: selectedContentType.value,
							eventBus: null,
							options: {
								hideReadOnly: true,
								hideModels: true,
								schemaKeyboardNav: true
							},
							schema: bodySchema.value
						}, null, 8, ["schema"]))])) : selectedContentType.value === "none" ? (openBlock(), createElementBlock("div", _hoisted_3, [createElementVNode("span", null, toDisplayString(unref(translate)("apiClient.requestBody.noBody")), 1)])) : selectedContentType.value === "application/octet-stream" ? (openBlock(), createElementBlock("div", _hoisted_4, [unref(getFileName)(unref(unpackProxyObject)(example.value?.value)) !== void 0 ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [createElementVNode("span", _hoisted_5, toDisplayString(unref(getFileName)(unref(unpackProxyObject)(example.value?.value))), 1), createVNode(unref(ScalarButton), {
							class: "bg-b-2 hover:bg-b-3 text-c-2 ml-1 border-0 shadow-none",
							size: "sm",
							variant: "outlined",
							onClick: _cache[3] || (_cache[3] = ($event) => emits("update:value", {
								payload: void 0,
								contentType: selectedContentType.value
							}))
						}, {
							default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.requestBody.delete")), 1)]),
							_: 1
						})], 64)) : (openBlock(), createBlock(unref(ScalarButton), {
							key: 1,
							class: "bg-b-2 hover:bg-b-3 text-c-2 border-0 shadow-none",
							size: "sm",
							variant: "outlined",
							onClick: _cache[4] || (_cache[4] = () => handleFileUpload((file) => emits("update:value", {
								payload: file,
								contentType: selectedContentType.value
							})))
						}, {
							default: withCtx(() => [createElementVNode("span", null, toDisplayString(unref(translate)("apiClient.requestBody.selectFile")), 1), createVNode(unref(ScalarIcon), {
								class: "ml-1",
								icon: "Upload",
								size: "xs",
								thickness: "2.5"
							})]),
							_: 1
						}))])) : selectedContentType.value === "multipart/form-data" || selectedContentType.value === "application/x-www-form-urlencoded" ? (openBlock(), createBlock(RequestBodyForm_default, {
							key: 3,
							bodySchema: bodySchema.value,
							compositionSelection: __props.requestBodyCompositionSelection,
							environment: __props.environment,
							example: example.value,
							selectedContentType: selectedContentType.value,
							"onUpdate:formValue": _cache[5] || (_cache[5] = (payload) => emits("update:formValue", {
								payload,
								contentType: selectedContentType.value
							}))
						}, null, 8, [
							"bodySchema",
							"compositionSelection",
							"environment",
							"example",
							"selectedContentType"
						])) : (openBlock(), createElementBlock(Fragment, { key: 4 }, [bodyView.value === "form" && isFormViewAvailable.value ? (openBlock(), createBlock(RequestBodyStructured_default, {
							key: 0,
							bodySchema: bodySchema.value,
							contentType: selectedContentType.value,
							environment: __props.environment,
							parsedValue: parsedBody.value.value,
							"onUpdate:value": _cache[6] || (_cache[6] = (value) => emits("update:value", {
								payload: value,
								contentType: selectedContentType.value
							}))
						}, null, 8, [
							"bodySchema",
							"contentType",
							"environment",
							"parsedValue"
						])) : (openBlock(), createElementBlock("div", _hoisted_6, [createVNode(unref(CodeInput_default), {
							class: "px-3",
							content: "",
							environment: __props.environment,
							language: selectedLanguage.value,
							lineNumbers: "",
							lint: "",
							modelValue: bodyValue.value,
							withFakeData: "",
							"onUpdate:modelValue": _cache[7] || (_cache[7] = (value) => emits("update:value", {
								payload: value,
								contentType: selectedContentType.value
							}))
						}, null, 8, [
							"environment",
							"language",
							"modelValue"
						]), bodyValue.value ? (openBlock(), createBlock(unref(ScalarCodeBlockCopy), {
							key: 0,
							class: "absolute top-1.5 right-1.5",
							content: bodyValue.value,
							lang: selectedLanguage.value,
							showLang: ""
						}, {
							backdrop: withCtx(() => [createVNode(unref(ScalarCopyBackdrop), { class: "-top-1 -right-1.5" })]),
							_: 1
						}, 8, ["content", "lang"])) : createCommentVNode("", true)]))], 64))]),
						_: 1
					})]),
					_: 1
				})]),
				_: 1
			});
		};
	}
});
//#endregion
export { RequestBody_vue_vue_type_script_setup_true_lang_default as default };

//# sourceMappingURL=RequestBody.vue_vue_type_script_setup_true_lang.js.map